<div class="dynamic-forms-setup min-vh-100">
  <HomeNavBar />
  <InactivityModal />

  <main class="container-fluid setup-page px-3 px-md-4 py-4">
     <button
        class="btn rounded-circle d-flex align-items-center justify-content-center membership-view-back-btn"
        @click="$router.back()"
      >
        <i class="bi bi-arrow-left fs-4"></i>
      </button>

    <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
      <div class="setup-heading">
        <h1 class="fw-bold mb-1">Dynamic Forms</h1>
        <p class="text-muted mb-0">Follow the setup steps, preview the form, publish it, and assign it to profiles.</p>
      </div>
    </div>

    <div class="setup-workflow-layout">
      <aside class="setup-stepper" aria-label="Dynamic forms setup steps">
        <div class="setup-stepper-heading">
          <strong>Setup progress</strong>
          <small>Complete each step in order</small>
        </div>
        <button v-for="step in steps" :key="step.number" type="button"
          :class="['setup-stepper-item', { active: active_tab === step.tab, complete: is_step_complete(step.tab) }]"
          :disabled="!can_open_step(step.tab)"
          @click="select_tab(step.tab)">
          <span class="setup-stepper-number">
            <i v-if="is_step_complete(step.tab)" class="bi bi-check-lg"></i>
            <span v-else>{{ step.number }}</span>
          </span>
          <span class="setup-stepper-copy">
            <strong>{{ step.title }}</strong>
            <small>{{ step.description }}</small>
          </span>
          <span class="setup-stepper-state">{{ active_tab === step.tab ? 'Current' : is_step_complete(step.tab) ? 'Done' : 'Pending' }}</span>
        </button>
      </aside>

      <div class="setup-workflow-content">

    <section v-if="active_tab === 'preview'" class="setup-panel mx-auto mt-4">
      <div v-if="!forms.length">
      <div class="text-center setup-empty-heading">
        <h2>No forms available yet</h2>
        <p>Complete Questions, Sections, and Forms before previewing your form.</p>
      </div>
      <div class="text-center mt-4">
        <button class="btn btn-primary rounded setup-continue" @click="select_tab('forms')">
          Go to Create Form
        </button>
      </div>
      </div>

      <div v-else>
        <div class="preview-toolbar">
          <div>
            <h2>Form Preview</h2>
            <p>Review how the selected form will appear to users.</p>
          </div>
          <div class="setup-field preview-form-select">
            <label for="preview_form">Form</label>
            <select id="preview_form" v-model="selected_preview_form_id" class="form-select" @change="load_form_preview">
              <option v-for="form in forms" :key="form.id" :value="form.id">{{ form.name || form.form_name }}</option>
            </select>
          </div>
        </div>

        <div class="preview-profile-switch" aria-label="Profile preview type">
          <button type="button" :class="{ active: preview_profile_mode === 'personal' }"
            @click="preview_profile_mode = 'personal'">
            <i class="bi bi-person"></i>Personal Profile
          </button>
          <button type="button" :class="{ active: preview_profile_mode === 'business' }"
            @click="preview_profile_mode = 'business'">
            <i class="bi bi-building"></i>Company Profile
          </button>
        </div>

        <div :class="['profile-preview-context', preview_profile_mode]">
          <div class="profile-preview-identity">
            <span class="profile-preview-avatar">
              <i :class="preview_profile_mode === 'personal' ? 'bi bi-person' : 'bi bi-building'"></i>
            </span>
            <span>
              <strong>{{ preview_profile_mode === 'personal' ? 'Personal Profile' : 'Company Profile' }}</strong>
              <small>{{ preview_profile_mode === 'personal' ? 'Previewing the form as an individual user' : 'Previewing the form as an organisation or company' }}</small>
            </span>
          </div>
        </div>

        <div v-if="preview_loading" class="setup-data-empty">
          <span class="spinner-border spinner-border-sm"></span> Loading form preview...
        </div>
        <div v-else-if="preview_sections.length" class="form-preview-sections">
          <section v-for="(section, section_index) in preview_sections" :key="section.id" class="form-preview-section">
            <h3><span>{{ section_index + 1 }}</span>{{ section.name }}</h3>
            <div class="form-preview-questions">
              <div v-for="(entry, question_index) in section.question_sections" :key="entry.id" class="form-preview-question">
                <label>
                  {{ question_index + 1 }}. {{ entry.question.question }}
                  <span v-if="entry.question.is_mandatory" class="text-danger">*</span>
                </label>
                <textarea v-if="get_preview_question_type(entry.question) === 'long_text'" class="form-control" rows="3" disabled></textarea>
                <input v-else-if="get_preview_question_type(entry.question) === 'number'" type="number" class="form-control" disabled>
                <input v-else-if="get_preview_question_type(entry.question) === 'file'" type="file" class="form-control" disabled>
                <select v-else-if="['single_select', 'selection', 'multiple_choice'].includes(get_preview_question_type(entry.question))"
                  class="form-select" disabled>
                  <option>Select an option</option>
                  <option v-for="option in entry.question.options" :key="option.id">{{ option.option || option.text }}</option>
                </select>
                <div v-else-if="get_preview_question_type(entry.question) === 'yes_no'" class="preview-choice-row">
                  <label><input type="radio" disabled> Yes</label><label><input type="radio" disabled> No</label>
                </div>
                <input v-else type="text" class="form-control" disabled>
              </div>
            </div>
          </section>
        </div>
        <div v-else class="setup-data-empty">
          <i class="bi bi-layout-text-window"></i>
          <strong>This form has no sections yet</strong>
          <span>Assign sections to the form from the Forms tab to build its preview.</span>
          <button class="btn btn-primary btn-sm rounded mt-2" @click="select_tab('forms')">Go to Forms</button>
        </div>
        <div class="workflow-continue-row">
          <button class="btn btn-light rounded workflow-previous-button" @click="go_to_previous_step">
            <i class="bi bi-arrow-left me-2"></i>Previous
          </button>
          <button class="btn btn-primary rounded" @click="continue_to_next_step">
            {{ get_continue_label() }}<i class="bi bi-arrow-right ms-2"></i>
          </button>
        </div>
      </div>
    </section>

    <section v-else class="setup-content-panel mx-auto mt-4">
      <div class="setup-content-header">
        <div>
          <h2>{{ tabs.find(tab => tab.key === active_tab)?.label }}</h2>
          <p v-if="active_tab === 'questions'">Create and manage reusable questions for your forms.</p>
          <p v-else-if="active_tab === 'sections'">Group and order related questions inside sections.</p>
          <p v-else-if="active_tab === 'forms'">Create forms and add your prepared sections.</p>
          <p v-else>Assign published forms to the appropriate profiles.</p>
        </div>
        <button v-if="['questions', 'sections', 'forms'].includes(active_tab)"
          class="btn btn-primary rounded text-xs" @click="close_assignment(); cancel_edit(); show_create_panel = !show_create_panel">
          <i class="bi bi-plus-lg me-1"></i>
          Create {{ active_tab === 'questions' ? 'Question' : active_tab === 'sections' ? 'Section' : 'Form' }}
        </button>
      </div>

      <div v-if="active_tab === 'questions'" class="data-collection-notice">
        <span class="data-collection-notice-icon"><i class="bi bi-database-check"></i></span>
        <div>
          <strong>Define the data you want to collect</strong>
          <p>
            Questions determine what information people provide and how that information is captured.
            Choose the correct answer type and mark important fields as mandatory to keep your collected data consistent and useful.
          </p>
        </div>
      </div>

      <div v-if="assignment_target" class="setup-create-panel setup-manage-assignment">
        <div class="setup-picker-heading w-100">
          <div>
            <strong v-if="assignment_type === 'questions'">Assign questions to {{ assignment_target.name }}</strong>
            <strong v-else-if="assignment_type === 'question_to_section'">Assign “{{ assignment_target.question }}” to a section</strong>
            <strong v-else>Assign sections to {{ assignment_target.name || assignment_target.form_name }}</strong>
            <small>Select the items to assign, then save your changes.</small>
          </div>
          <button type="button" class="btn-close" aria-label="Close" @click="close_assignment"></button>
        </div>

        <div v-if="assignment_type === 'question_to_section'" class="setup-field w-100">
          <label for="assignment_section">Section</label>
          <select id="assignment_section" v-model="assignment_section_id" class="form-select" required>
            <option value="" disabled>Select a section</option>
            <option v-for="section in sections" :key="section.id" :value="section.id">{{ section.name }}</option>
          </select>
          <small v-if="!sections.length" class="text-muted">Create a section before assigning this question.</small>
        </div>
        <div v-else-if="assignment_type === 'questions'" class="setup-picker-list w-100">
          <label v-for="question in questions" :key="question.id" class="setup-picker-item">
            <input v-model="selected_question_ids" type="checkbox" :value="question.id">
            <span class="setup-data-number">{{ question.question_number || '—' }}</span>
            <span>{{ question.question }}</span>
            <small class="ms-auto">{{ question.question_type?.question_type?.replaceAll('_', ' ') }}</small>
          </label>
        </div>
        <div v-else class="setup-picker-list w-100">
          <label v-for="section in sections" :key="section.id" class="setup-picker-item">
            <input v-model="selected_section_ids" type="checkbox" :value="section.id">
            <i class="bi bi-layout-text-sidebar-reverse"></i>
            <span>{{ section.name }}</span>
          </label>
        </div>

        <div class="setup-form-actions w-100 justify-content-end">
          <button type="button" class="btn btn-light rounded" @click="close_assignment">Cancel</button>
          <button type="button" class="btn btn-primary rounded" @click="save_assignment">
            Save Assignment
          </button>
        </div>
      </div>

      <form v-if="show_create_panel" :class="['setup-create-panel', { 'setup-question-form': active_tab === 'questions' }]" @submit.prevent="create_item">
        <div v-if="active_tab === 'questions'" class="setup-create-fields">
          <fieldset class="setup-question-fieldset">
            <legend>{{ editing_id ? 'Edit Question' : 'Create Question' }}</legend>
            <div class="setup-question-grid">
              <div class="setup-field">
                <label>Question<span class="text-danger">*</span></label>
                <input v-model="question_form.text" :class="['form-control', { 'is-invalid': validation_errors.question }]" placeholder="Enter question text" required>
                <small v-if="validation_errors.question" class="setup-validation-error">{{ validation_errors.question }}</small>
              </div>
              <div class="setup-field">
                <label>Question Number<span class="text-danger">*</span></label>
                <input v-model="question_form.number" type="number" :class="['form-control', { 'is-invalid': validation_errors.question_number }]" placeholder="Eg. 1" required>
                <small v-if="validation_errors.question_number" class="setup-validation-error">{{ validation_errors.question_number }}</small>
              </div>
              <div class="setup-field">
                <label>Type<span class="text-danger">*</span></label>
                <select v-model="question_form.type" :class="['form-select', { 'is-invalid': validation_errors.question_type }]" required @change="handle_question_type_change">
                  <option value="" disabled>Select</option>
                  <option v-for="type in question_types" :key="type.id" :value="type.id">
                    {{ type.question_type || type.name }}
                  </option>
                </select>
                <small v-if="validation_errors.question_type" class="setup-validation-error">{{ validation_errors.question_type }}</small>
              </div>
              <div class="setup-field">
                <label>Is the question mandatory?</label>
                <select v-model="question_form.mandatory" class="form-select">
                  <option :value="false">No</option>
                  <option :value="true">Yes</option>
                </select>
              </div>
              <div class="setup-field">
                <label>Is the question locked?</label>
                <select v-model="question_form.locked" class="form-select">
                  <option :value="false">No</option>
                  <option :value="true">Yes</option>
                </select>
              </div>
            </div>
          <div v-if="is_multiple_choice()" class="setup-options mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label>Answer options</label>
              <button type="button" class="btn btn-sm btn-outline-primary" @click="add_question_option">
                <i class="bi bi-plus-lg"></i> Add option
              </button>
            </div>
            <small v-if="validation_errors.options" class="setup-validation-error">{{ validation_errors.options }}</small>
            <div v-for="(option, index) in question_form.options" :key="index" class="setup-option-row">
              <input v-model="option.text" class="form-control" :placeholder="`Option ${index + 1}`" required>
              <button type="button" class="btn btn-light" aria-label="Remove option" @click="remove_question_option(index)">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
          <div class="setup-form-actions setup-question-actions">
            <button type="button" class="btn btn-light rounded" @click="show_create_panel = false; cancel_edit()">Cancel</button>
            <button type="submit" class="btn btn-primary rounded">{{ editing_id ? 'Update' : 'Save' }}</button>
          </div>
          </fieldset>
        </div>

        <div v-else-if="active_tab === 'sections'" class="setup-create-fields">
          <fieldset class="setup-question-fieldset setup-entity-fieldset">
            <legend>{{ editing_id ? 'Edit Section' : 'Create Section' }}</legend>
            <div class="setup-question-grid">
              <div class="setup-field">
                <label>Section Name<span class="text-danger">*</span></label>
                <input v-model="section_form.name" :class="['form-control', { 'is-invalid': validation_errors.section_name }]" placeholder="Enter section name" required>
                <small v-if="validation_errors.section_name" class="setup-validation-error">{{ validation_errors.section_name }}</small>
              </div>
              <div class="setup-field">
                <label>Is the section locked?</label>
                <select v-model="section_form.locked" class="form-select">
                  <option :value="false">No</option>
                  <option :value="true">Yes</option>
                </select>
              </div>
            </div>
          <div class="setup-assignment-picker mt-4">
            <div class="setup-picker-heading">
              <div><strong>Assign questions</strong><small>Select the questions to include in this section.</small></div>
              <span>{{ selected_question_ids.length }} selected</span>
            </div>
            <div v-if="questions.length" class="setup-picker-list">
              <label v-for="question in questions" :key="question.id" class="setup-picker-item">
                <input v-model="selected_question_ids" type="checkbox" :value="question.id">
                <span class="setup-data-number">{{ question.question_number || '—' }}</span>
                <span>{{ question.question }}</span>
              </label>
            </div>
            <p v-else class="setup-picker-empty">Create questions first, then return here to assign them.</p>
            <small v-if="validation_errors.section_questions" class="setup-validation-error">{{ validation_errors.section_questions }}</small>
          </div>
            <div class="setup-form-actions setup-question-actions">
              <button type="button" class="btn btn-light rounded" @click="show_create_panel = false; cancel_edit()">Cancel</button>
              <button type="submit" class="btn btn-primary rounded">{{ editing_id ? 'Update' : 'Save' }}</button>
            </div>
          </fieldset>
        </div>

        <div v-else-if="active_tab === 'forms'" class="setup-create-fields">
          <fieldset class="setup-question-fieldset setup-entity-fieldset">
            <legend>{{ editing_id ? 'Edit Form' : 'Create Form' }}</legend>
            <div class="setup-question-grid">
              <div class="setup-field">
                <label>Form Name<span class="text-danger">*</span></label>
                <input v-model="dynamic_form.name" :class="['form-control', { 'is-invalid': validation_errors.form_name }]" placeholder="Enter form name" required>
                <small v-if="validation_errors.form_name" class="setup-validation-error">{{ validation_errors.form_name }}</small>
              </div>
              <div class="setup-field">
                <label>Description</label>
                <input v-model="dynamic_form.description" class="form-control" placeholder="Describe this form">
              </div>
            </div>
          <div class="setup-assignment-picker mt-4">
            <div class="setup-picker-heading">
              <div><strong>Assign sections</strong><small>Select the sections to include in this form.</small></div>
              <span>{{ selected_section_ids.length }} selected</span>
            </div>
            <div v-if="sections.length" class="setup-picker-list">
              <label v-for="section in sections" :key="section.id" class="setup-picker-item">
                <input v-model="selected_section_ids" type="checkbox" :value="section.id">
                <i class="bi bi-layout-text-sidebar-reverse"></i>
                <span>{{ section.name }}</span>
              </label>
            </div>
            <p v-else class="setup-picker-empty">Create sections first, then return here to assign them.</p>
            <small v-if="validation_errors.form_sections" class="setup-validation-error">{{ validation_errors.form_sections }}</small>
          </div>
            <div class="setup-form-actions setup-question-actions">
              <button type="button" class="btn btn-light rounded" @click="show_create_panel = false; cancel_edit()">Cancel</button>
              <button type="submit" class="btn btn-primary rounded">{{ editing_id ? 'Update' : 'Save' }}</button>
            </div>
          </fieldset>
        </div>
      </form>

      <div v-if="active_tab === 'assignments' && profile_assignment_form" class="profile-assignment-panel">
        <div class="setup-picker-heading">
          <div>
            <strong>Assign {{ profile_assignment_form.name || profile_assignment_form.form_name }}</strong>
            <small>Select the personal profile types that should receive this form.</small>
          </div>
          <button type="button" class="btn-close" aria-label="Close" @click="close_profile_assignment"></button>
        </div>

        <div v-if="profile_types.length" class="profile-type-grid">
          <label v-for="profile in profile_types" :key="profile.id"
            :class="['profile-type-option', { selected: selected_profile_ids.includes(profile.id) }]">
            <input v-model="selected_profile_ids" type="checkbox" :value="profile.id">
            <span class="profile-type-icon"><i class="bi bi-person"></i></span>
            <span><strong>{{ profile.name }}</strong><small>Personal profile</small></span>
          </label>
        </div>
        <div v-else class="setup-picker-empty">No personal profile types are available.</div>

        <div class="setup-form-actions justify-content-end mt-3">
          <button type="button" class="btn btn-light rounded" @click="close_profile_assignment">Cancel</button>
          <button type="button" class="btn btn-primary rounded" :disabled="saving_profile_assignment"
            @click="save_profile_assignment">
            <span v-if="saving_profile_assignment" class="spinner-border spinner-border-sm me-1"></span>
            Save Assignments
          </button>
        </div>
      </div>

      <div v-if="loading" class="setup-data-empty"><span class="spinner-border spinner-border-sm"></span> Loading...</div>

      <div v-else-if="active_tab === 'questions' && questions.length" class="setup-data-list">
        <div v-for="question in questions" :key="question.id" class="setup-data-row">
          <span class="setup-data-number">{{ question.question_number || '—' }}</span>
          <div>
            <strong>{{ question.question }}</strong>
            <span class="setup-question-meta d-none">
              <small class="setup-type-badge">{{ (question.question_type?.question_type || 'Question').replaceAll('_', ' ') }}</small>
              <small :class="['setup-state-badge', question.is_mandatory ? 'required' : 'optional']">
                {{ question.is_mandatory ? 'Mandatory' : 'Optional' }}
              </small>
              <small v-if="question.is_locked" class="setup-state-badge locked"><i class="bi bi-lock-fill"></i> Locked</small>
            </span>
          </div>
          <span v-if="question.options?.length" class="setup-option-count">{{ question.options.length }} options</span>
          <button class="btn btn-light btn-sm rounded text-primary border ms-auto"
            @click="open_question_section_assignment(question)">
            <i class="bi bi-diagram-3 me-1"></i>Assign to Section
          </button>
          <button class="btn btn-light btn-sm rounded text-success border" @click="edit_question(question)">
            <i class="bi bi-pencil-square me-1"></i>Edit
          </button>
          <button class="btn btn-light btn-sm rounded text-danger border" @click="delete_item('questions', question.id)">
            <i class="bi bi-trash me-1"></i>Delete
          </button>
        </div>
      </div>
      <div v-else-if="active_tab === 'sections' && sections.length" class="setup-data-list">
        <div v-for="section in sections" :key="section.id" class="setup-data-row">
          <i class="bi bi-layout-text-sidebar-reverse"></i><div><strong>{{ section.name }}</strong><small>Section</small></div>
          <button class="btn btn-light btn-sm rounded text-primary border ms-auto"
            @click="open_question_assignment(section)">
            <i class="bi bi-diagram-3 me-1"></i>Assign Questions
          </button>
          <button class="btn btn-light btn-sm rounded text-success border" @click="edit_section(section)">
            <i class="bi bi-pencil-square me-1"></i>Edit
          </button>
          <button class="btn btn-light btn-sm rounded text-danger border" @click="delete_item('sections', section.id)">
            <i class="bi bi-trash me-1"></i>Delete
          </button>
        </div>
      </div>
      <div v-else-if="['forms', 'assignments'].includes(active_tab) && forms.length" class="setup-data-list">
        <div v-for="form in forms" :key="form.id" class="setup-data-row">
          <i class="bi bi-ui-checks"></i><div><strong>{{ form.name || form.form_name }}</strong><small>{{ form.description || 'Dynamic form' }}</small>
            <span v-if="active_tab === 'assignments' && form.assigned_profile_types?.length" class="assigned-profile-list">
              <small v-for="profile_name in form.assigned_profile_types" :key="profile_name">{{ profile_name }}</small>
            </span>
          </div>
          <button v-if="active_tab === 'forms'" class="btn btn-light btn-sm rounded text-primary border ms-auto"
            @click="open_section_assignment(form)">
            <i class="bi bi-diagram-3 me-1"></i>Assign Sections
          </button>
          <button v-if="active_tab === 'forms'" class="btn btn-light btn-sm rounded text-success border" @click="edit_form(form)">
            <i class="bi bi-pencil-square me-1"></i>Edit
          </button>
          <button v-if="active_tab === 'forms'" class="btn btn-light btn-sm rounded text-danger border" @click="delete_item('forms', form.id)">
            <i class="bi bi-trash me-1"></i>Delete
          </button>
          <button v-else class="btn btn-outline-primary btn-sm ms-auto" @click="open_profile_assignment(form)">
            <i class="bi bi-person-plus me-1"></i>Assign Profiles
          </button>
        </div>
      </div>
      <div v-else class="setup-data-empty">
        <i class="bi bi-inbox"></i>
        <strong>No {{ active_tab }} available yet</strong>
        <span>Use the create button to add your first item.</span>
      </div>
      <div class="workflow-continue-row">
        <small v-if="!can_continue_step()">Create at least one item to continue.</small>
        <button v-if="active_tab !== 'questions'" class="btn btn-light rounded workflow-previous-button"
          @click="go_to_previous_step">
          <i class="bi bi-arrow-left me-2"></i>Previous
        </button>
        <button class="btn btn-primary rounded" :disabled="!can_continue_step()" @click="continue_to_next_step">
          {{ get_continue_label() }}<i class="bi bi-arrow-right ms-2"></i>
        </button>
      </div>
    </section>
      </div>
    </div>
  </main>
</div>
